import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import api, { errMsg } from "@/lib/api";
import { formatAmount } from "@/lib/format";
import ZButton from "@/components/ZButton";
import { ZInput } from "@/components/ZInput";
import Modal from "@/components/Modal";
import { ScanLine, CheckCircle2, XCircle, AlertTriangle, Ban } from "lucide-react";

const RESULT_ICON = {
  ok: { icon: <CheckCircle2 size={36} />, color: "var(--z-success)" },
  insufficient: { icon: <AlertTriangle size={36} />, color: "var(--z-orange)" },
  fully_used: { icon: <AlertTriangle size={36} />, color: "var(--z-orange)" },
  already_used: { icon: <AlertTriangle size={36} />, color: "var(--z-orange)" },
  expired: { icon: <AlertTriangle size={36} />, color: "var(--z-orange)" },
  unauthorized: { icon: <Ban size={36} />, color: "var(--z-error)" },
  unclaimed: { icon: <AlertTriangle size={36} />, color: "var(--z-orange)" },
  invalid: { icon: <XCircle size={36} />, color: "var(--z-error)" },
};

export default function VoucherScanPage() {
  const [scanning, setScanning] = useState(false);
  const [voucher, setVoucher] = useState(null);
  const [result, setResult] = useState(null);
  const [manual, setManual] = useState("");
  const [amount, setAmount] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const scannerRef = useRef(null);

  const onScanned = async (text) => {
    await stopScanner();
    // Just preview the voucher first (without redeeming)
    try {
      const r = await api.post("/vouchers/redeem", { payload: text, amount: 0.01, item_ids: [] });
      setResult(r.data);
    } catch (e) {
      setResult({ result: "invalid", message: errMsg(e) });
    }
  };

  // Better: fetch voucher then let merchant choose, then redeem
  const fetchVoucher = async (payload) => {
    const parts = payload.split("|");
    if (parts.length !== 4 || parts[0] !== "ZEVV") {
      setResult({ result: "invalid", message: "QR non reconnu." });
      return;
    }
    try {
      const r = await api.get(`/vouchers/${parts[2]}?token=${encodeURIComponent(payload)}`);
      setVoucher({ ...r.data, _payload: payload });
      setAmount("");
      setSelectedItems([]);
    } catch (e) {
      setResult({ result: "invalid", message: errMsg(e) });
    }
  };

  const startScanner = async () => {
    if (scanning) return;
    setResult(null); setVoucher(null);
    setScanning(true);
    try {
      const qr = new Html5Qrcode("vqr-reader");
      scannerRef.current = qr;
      await qr.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 260, height: 260 } },
        async (text) => { await stopScanner(); fetchVoucher(text); },
        () => {},
      );
    } catch (e) {
      setScanning(false);
      setResult({ result: "invalid", message: "Caméra indisponible. Utilisez la saisie manuelle." });
    }
  };

  const stopScanner = async () => {
    try {
      if (scannerRef.current && scanning) {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      }
    } catch (e) {}
    scannerRef.current = null;
    setScanning(false);
  };
  useEffect(() => () => stopScanner(), []); // eslint-disable-line

  const submitManual = () => {
    fetchVoucher(manual.trim());
    setManual("");
  };

  const doRedeem = async () => {
    if (!voucher) return;
    setBusy(true);
    try {
      const payload = { payload: voucher._payload };
      if (voucher.type === "amount") {
        const a = parseFloat(amount);
        if (!a || a <= 0) { setBusy(false); return setResult({ result: "invalid", message: "Montant invalide." }); }
        payload.amount = a;
      } else {
        if (selectedItems.length === 0) { setBusy(false); return setResult({ result: "invalid", message: "Sélectionnez au moins un article." }); }
        payload.item_ids = selectedItems;
      }
      const r = await api.post("/vouchers/redeem", payload);
      setResult(r.data);
      setVoucher(null);
    } catch (e) {
      setResult({ result: "invalid", message: errMsg(e) });
    } finally { setBusy(false); }
  };

  return (
    <div className="z-container" style={{ maxWidth: 540 }} data-testid="voucher-scan-page">
      <h1 className="z-page-title">Scanner un bon d'achat</h1>
      <p className="z-page-sub">Validez le bon présenté par le client.</p>

      <div id="vqr-reader" style={{ background: "#000", minHeight: scanning ? 280 : 60, borderRadius: 16, overflow: "hidden", marginBottom: 14 }} />

      {!scanning ? (
        <ZButton onClick={startScanner} block data-testid="vs-start"><ScanLine size={16} /> Démarrer le scanner</ZButton>
      ) : (
        <ZButton variant="outline" onClick={stopScanner} block data-testid="vs-stop">Arrêter</ZButton>
      )}

      <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
        <input className="z-input" value={manual} onChange={(e) => setManual(e.target.value)} placeholder="Coller le payload ZEVV|... ou saisir un code" data-testid="vs-manual-input" />
        <ZButton variant="outline" onClick={submitManual} disabled={!manual.trim()} data-testid="vs-manual-submit">Valider</ZButton>
      </div>

      {/* Voucher loaded — show redeem form */}
      {voucher && (
        <div className="z-card" style={{ padding: 18, marginTop: 18 }} data-testid="voucher-loaded">
          <h3 style={{ margin: "0 0 6px", fontSize: 17, fontWeight: 700 }}>{voucher.title}</h3>
          <div style={{ fontSize: 13, color: "var(--z-muted)", marginBottom: 12 }}>
            {voucher.owner_name} ({voucher.owner_email}) · Code <code>{voucher.code}</code>
          </div>

          {voucher.type === "amount" ? (
            <>
              <div style={{ background: "var(--z-bg)", padding: 12, borderRadius: 10, marginBottom: 14 }}>
                <div style={{ fontSize: 13, color: "var(--z-muted)" }}>Solde disponible</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: "var(--z-teal)" }}>
                  {formatAmount(voucher.remaining_amount, voucher.currency)}
                </div>
              </div>
              <ZInput type="number" label="Montant à débiter" min="0" step="100" value={amount} onChange={(e) => setAmount(e.target.value)} required data-testid="redeem-amount" />
            </>
          ) : (
            <>
              <p style={{ fontSize: 13, color: "var(--z-muted)", marginBottom: 8 }}>Sélectionnez les articles utilisés :</p>
              <div style={{ display: "grid", gap: 8, marginBottom: 14 }}>
                {voucher.items.map((it) => {
                  const remaining = it.quantity - it.used_quantity;
                  const disabled = remaining <= 0;
                  const active = selectedItems.includes(it.id);
                  return (
                    <button key={it.id} type="button" disabled={disabled}
                      onClick={() => setSelectedItems(active ? selectedItems.filter((x) => x !== it.id) : [...selectedItems, it.id])}
                      className="z-chip" style={{ justifyContent: "space-between", padding: "12px 14px", opacity: disabled ? 0.5 : 1, ...(active ? { background: "var(--z-teal)", color: "white", borderColor: "var(--z-teal)" } : {}) }}
                      data-testid={`item-${it.id}`}
                    >
                      <span>{it.label}</span>
                      <span>{remaining} / {it.quantity}</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          <div style={{ display: "flex", gap: 8 }}>
            <ZButton variant="outline" block onClick={() => setVoucher(null)} data-testid="cancel-redeem">Annuler</ZButton>
            <ZButton block onClick={doRedeem} loading={busy} data-testid="confirm-redeem">Valider</ZButton>
          </div>
        </div>
      )}

      <Modal open={!!result} onClose={() => setResult(null)} title="Résultat" testId="redeem-result">
        {result && (
          <div style={{ textAlign: "center" }}>
            <div style={{ color: RESULT_ICON[result.result]?.color || "var(--z-error)", marginBottom: 12 }}>
              {RESULT_ICON[result.result]?.icon || <XCircle size={36} />}
            </div>
            <p style={{ marginBottom: 18 }} data-testid="result-message">{result.message}</p>
            <ZButton block onClick={() => { setResult(null); startScanner(); }}>Scanner le suivant</ZButton>
          </div>
        )}
      </Modal>
    </div>
  );
}
